<template>
  <div class="palette-menu-item-wrapper">
    <!-- Standard Menu Item -->
    <v-list-item
      v-if="!hasSubmenu"
      class="palette-menu-item"
      :class="{
        'is-active': isActive,
        'is-disabled': isDisabled || !hasPermission,
      }"
      :disabled="isDisabled || !hasPermission"
      @click="handleClick"
      role="menuitem"
      :tabindex="isDisabled ? -1 : 0"
      @keydown.enter.prevent="handleClick"
      @keydown.space.prevent="handleClick"
    >
      <v-list-item-icon class="mr-3">
        <slot name="icon" :icon="item.icon">
          <v-icon :size="18" :color="iconColor">{{ item.icon }}</v-icon>
        </slot>
      </v-list-item-icon>

      <v-list-item-content>
        <v-list-item-title class="item-title">
          {{ item.title }}
        </v-list-item-title>
      </v-list-item-content>

      <!-- Optional badge slot -->
      <v-list-item-action v-if="$slots.badge" class="ml-2">
        <slot name="badge" />
      </v-list-item-action>

      <!-- Disabled indicator -->
      <v-list-item-action v-if="!hasPermission" class="ml-2">
        <v-icon size="14" color="grey">mdi-lock</v-icon>
      </v-list-item-action>
    </v-list-item>

    <!-- Submenu Item (Task Boards, Registers) -->
    <v-list-group
      v-else
      v-model="isExpanded"
      class="palette-submenu"
      :class="{ 'is-disabled': isDisabled || !hasPermission }"
      :disabled="isDisabled || !hasPermission"
      no-action
    >
      <template v-slot:activator>
        <v-list-item-icon class="mr-3">
          <v-icon :size="18" :color="iconColor">{{ item.icon }}</v-icon>
        </v-list-item-icon>

        <v-list-item-content>
          <v-list-item-title class="item-title">
            {{ item.title }}
          </v-list-item-title>
        </v-list-item-content>
      </template>

      <!-- Dynamic submenu items -->
      <template v-if="submenuItems && submenuItems.length > 0">
        <v-list-item
          v-for="subItem in submenuItems"
          :key="subItem.id"
          class="palette-submenu-item"
          @click="handleSubmenuClick(subItem)"
        >
          <v-list-item-icon class="mr-3">
            <v-icon :size="16" color="grey">{{ subItem.icon || 'mdi-circle-small' }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="submenu-title">
              {{ subItem.title || subItem.name }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <!-- Empty state -->
      <v-list-item v-else class="palette-submenu-item empty-state">
        <v-list-item-icon class="mr-3">
          <v-icon :size="16" color="grey lighten-1">mdi-information-outline</v-icon>
        </v-list-item-icon>

        <v-list-item-content>
          <v-list-item-title class="submenu-title text--disabled">
            {{ emptyMessage }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list-group>
  </div>
</template>

<script>
import { hasMenuPermission } from '@/utils/menuPermission';
import { $has } from '@/utils';

export default {
  name: 'PaletteMenuItem',

  props: {
    /**
     * Menu item configuration object
     * @type {Object}
     */
    item: {
      type: Object,
      required: true,
    },

    /**
     * Whether this item is currently active/selected
     */
    isActive: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether this item is disabled
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Dynamic submenu items (for Task Boards, Registers)
     * If provided, overrides the item.submenuGetter lookup
     */
    submenuItems: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      isExpanded: false,
    };
  },

  computed: {
    /**
     * Check if item has a submenu
     */
    hasSubmenu() {
      return this.item.type === 'submenu';
    },

    /**
     * Check user permission for this item
     */
    hasPermission() {
      if (!this.item.permission) {
        return true;
      }

      const permissions = Array.isArray(this.item.permission)
        ? this.item.permission
        : [this.item.permission];

      // Check if user has any of the required permissions
      return hasMenuPermission(permissions, this.$has);
    },

    /**
     * Icon color based on state
     */
    iconColor() {
      if (!this.hasPermission || this.isDisabled) {
        return 'grey lighten-1';
      }
      if (this.isActive) {
        return 'primary';
      }
      return 'grey';
    },

    /**
     * Empty state message for submenus
     */
    emptyMessage() {
      if (this.item.id === 'workspaces') {
        return 'No workspaces available';
      }
      if (this.item.id === 'registers') {
        return 'No registers available';
      }
      return 'No items available';
    },
  },

  methods: {
    $has,

    /**
     * Handle main item click
     */
    handleClick() {
      if (this.isDisabled || !this.hasPermission) {
        return;
      }

      this.$emit('click', { itemId: this.item.id, item: this.item });
    },

    /**
     * Handle submenu item click (Workspace or Register)
     */
    handleSubmenuClick(subItem) {
      // Build route based on item type
      const facilityId = this.$route.params.id;
      let route = null;

      if (this.item.id === 'workspaces') {
        // Workspace (formerly Task Board): /{facilityId}/{taskId}
        route = `/${facilityId}/${subItem.id}`;
      } else if (this.item.id === 'registers') {
        // Register: /{facilityId}/registers/{registerId}
        route = `/${facilityId}/registers/${subItem.id}`;
      }

      if (route) {
        this.$router.push(route);
      }

      this.$emit('submenu-click', {
        parentItemId: this.item.id,
        subItem,
        route,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.palette-menu-item-wrapper {
  width: 100%;
}

.palette-menu-item {
  min-height: 44px;
  padding: 0 var(--ds-space-md);
  border-radius: var(--ds-radius-md);
  margin: 2px var(--ds-space-sm);
  transition: all var(--ds-transition-fast);

  &:hover:not(.is-disabled) {
    background-color: var(--ds-surface-hover);
  }

  &.is-active {
    background-color: var(--ds-surface-active);

    // Left border indicator
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 20px;
      background-color: var(--v-primary-base);
      border-radius: 0 var(--ds-radius-sm) var(--ds-radius-sm) 0;
    }

    .item-title {
      color: var(--v-primary-base);
      font-weight: 500;
    }
  }

  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .item-title {
    font-size: var(--ds-font-body-sm);
    font-weight: 400;
    color: rgba(255, 255, 255, 0.87);
    letter-spacing: 0.5px;
  }
}

.palette-submenu {
  margin: 2px var(--ds-space-sm);

  &.is-disabled {
    opacity: 0.5;
  }

  // Override Vuetify group header styles
  ::v-deep .v-list-group__header {
    min-height: 44px;
    padding: 0 var(--ds-space-md);
    border-radius: var(--ds-radius-md);

    &:hover {
      background-color: var(--ds-surface-hover);
    }

    .item-title {
      font-size: var(--ds-font-body-sm);
      font-weight: 400;
      color: rgba(255, 255, 255, 0.87);
      letter-spacing: 0.5px;
    }
  }

  // Submenu items
  .palette-submenu-item {
    min-height: 40px;
    padding: 0 var(--ds-space-md) 0 var(--ds-space-xl);
    border-radius: var(--ds-radius-sm);
    margin: 1px 0;

    &:hover:not(.empty-state) {
      background-color: var(--ds-surface-hover);
    }

    &.empty-state {
      cursor: default;
    }

    .submenu-title {
      font-size: var(--ds-font-caption);
      font-weight: 400;
      color: rgba(255, 255, 255, 0.7);
    }
  }
}

// Focus styles for keyboard navigation
.palette-menu-item:focus,
.palette-submenu-item:focus {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--v-primary-base);
}
</style>
